import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { toast } from 'sonner';
import { humanizeError } from '../utils/errorHandling';
import { getApiBase } from '../utils/apiBase';
import { TurnstileWidget } from '../components/TurnstileWidget';
import { AuthPageLayout } from '../components/layout/AuthPageLayout';

const API = getApiBase();
const TURNSTILE_SITE_KEY = (process.env.REACT_APP_TURNSTILE_SITE_KEY || '').trim();
const TURNSTILE_ENABLED = Boolean(TURNSTILE_SITE_KEY);

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (TURNSTILE_ENABLED && !turnstileToken) {
      toast.error('Please complete the security check.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, {
        email: email.trim(),
        turnstile_token: turnstileToken || undefined,
      });
      setSubmitted(true);
      toast.success(data?.message || 'Check your email for next steps.');
    } catch (error) {
      toast.error(humanizeError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageLayout>
          <div className="mb-8 text-center">
            <Link to="/home" className="inline-flex flex-col items-center gap-3 transition-opacity hover:opacity-90">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#c9a961]/20 text-sm font-medium text-[#5c4a32] ring-1 ring-[#c9a961]/30">
                G
              </span>
              <h1 className="font-serif text-2xl font-medium tracking-tight text-[#1c1917]">Reset your password</h1>
              <p className="mt-2 text-sm text-[#5c534a]">We&apos;ll email you a link to choose a new password</p>
            </Link>
          </div>

          <Card className="rounded-2xl border border-[#e8e2d8]/90 bg-[#fffcf7]/95 shadow-sm">
            <CardHeader>
              <CardTitle className="font-serif text-xl font-normal">Forgot password</CardTitle>
              <CardDescription>Enter the email you used to register</CardDescription>
            </CardHeader>
            <CardContent>
              {submitted ? (
                <p className="text-sm leading-relaxed text-[#5c534a]">
                  If an account exists for that address, we sent instructions. You can close this page.
                </p>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="fp-email">Email</Label>
                    <Input
                      id="fp-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="border-[#e8e2d8] bg-white/90"
                    />
                  </div>
                  <TurnstileWidget siteKey={TURNSTILE_SITE_KEY || null} onToken={setTurnstileToken} className="flex justify-center py-1" />
                  <Button type="submit" className="w-full rounded-full" disabled={loading}>
                    {loading ? 'Sending…' : 'Send reset link'}
                  </Button>
                </form>
              )}
            </CardContent>
            <CardFooter className="flex flex-col gap-2 border-t border-[#e8e2d8]/60 pt-4">
              <Link to="/login" className="text-center text-sm text-[#5c4a32] underline underline-offset-2">
                Back to sign in
              </Link>
            </CardFooter>
          </Card>
    </AuthPageLayout>
  );
};

export default ForgotPassword;
